import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AuthProvider } from './context/AuthContext';
import AdminRoute from './components/AdminRoute';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import About from './components/About';
import Donate from './components/Donate';
import Contact from './components/Contact';
import Footer from './components/Footer';
import Login from './pages/Login';
import Admin from './pages/Admin';
import QuickAddEvent from './pages/QuickAddEvent';
import Legal from './pages/Legal';
import NewsArchive from './pages/NewsArchive';
import NewsDetail from './pages/NewsDetail';
import Events from './components/Events';
import Volunteer from './components/Volunteer';
import Partners from './components/Partners';
import { Section } from './components/ui';

const News = lazy(() => import('./components/News'));

const IntroLoader = ({ visible }: { visible: boolean }) => (
  <motion.div
    initial={{ opacity: 1 }}
    animate={{ opacity: visible ? 1 : 0 }}
    transition={{ duration: 0.5, ease: 'easeOut' }}
    className={`fixed inset-0 z-[80] flex items-center justify-center bg-paper ${visible ? 'pointer-events-auto' : 'pointer-events-none'}`}
    aria-hidden={!visible}
  >
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className="text-center"
    >
      <img src="/images/nousrire_logo.svg" alt="" className="h-20 w-auto mx-auto mb-4" />
      <p className="heading text-3xl md:text-4xl text-accent">Nous'Rire</p>
    </motion.div>
  </motion.div>
);

const HomePage = () => (
  <main className="relative">
    <Section id="accueil"><Hero /></Section>
    <Section id="mission"><About /></Section>
    <Section id="actions" className="bg-sun-soft">
      <Suspense fallback={<div className="py-24 text-center text-brand-pink-600">Chargement...</div>}>
        <News />
      </Suspense>
    </Section>
    <Section id="calendrier"><Events /></Section>
    <Section id="benevole" className="bg-sun-soft"><Volunteer /></Section>
    <Section id="partenaires"><Partners /></Section>
    <Section id="don" className="bg-sun text-[#2B1A18]"><Donate /></Section>
    <Section id="contact"><Contact /></Section>
  </main>
);

const AppContent = () => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const [isDarkMode, setIsDarkMode] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const savedTheme = localStorage.getItem('nr-theme');
    if (savedTheme) return savedTheme === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const [showIntro, setShowIntro] = useState(() => {
    if (typeof window === 'undefined') return false;
    return isHomePage && sessionStorage.getItem('nr-intro-seen') !== '1';
  });

  useEffect(() => {
    document.body.classList.toggle('theme-dark', isDarkMode);
    localStorage.setItem('nr-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  useEffect(() => {
    if (!isHomePage) {
      setShowIntro(false);
      return;
    }

    if (sessionStorage.getItem('nr-intro-seen') === '1') {
      setShowIntro(false);
      return;
    }

    const timer = window.setTimeout(() => {
      sessionStorage.setItem('nr-intro-seen', '1');
      setShowIntro(false);
    }, 1400);

    return () => window.clearTimeout(timer);
  }, [isHomePage]);

  return (
    <div className="app-shell min-h-screen bg-paper relative">
      <IntroLoader visible={showIntro} />
      {isHomePage && <Navbar isDarkMode={isDarkMode} onToggleTheme={toggleTheme} />}
      <Routes location={location}>
        <Route path="/" element={<HomePage />} />
        <Route path="/mentions-legales" element={<Legal />} />
        <Route path="/actualites" element={<NewsArchive />} />
        <Route path="/actualites/:id" element={<NewsDetail />} />
        <Route path="/login" element={<Login />} />
        <Route path="/admin" element={
          <AdminRoute>
            <Admin />
          </AdminRoute>
        } />
        <Route path="/admin/ajout" element={
          <AdminRoute>
            <QuickAddEvent />
          </AdminRoute>
        } />
      </Routes>
      {isHomePage && <Footer />}
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
};

export default App;
